import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { CASL_ORG } from "@/components/gavion/domain/email";
import { UNSUBSCRIBE_COPY, type UnsubscribeLang } from "./copy";

type Officer = { name: string | null; email: string | null };
type Notice = "rate_limited" | "error" | null;
type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  // CASL: `ready` is reachable even when the probe failed (masked is null): never lock out.
  | { kind: "ready"; masked: string | null; busy: boolean; notice: Notice }
  | { kind: "done" };

const base = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const NO_OFFICER: Officer = { name: null, email: null };

/**
 * Public unsubscribe page (spec 08 §7, CASL). Never unsubscribes on load (link scanners):
 * the recipient confirms with an explicit button. Language comes only from `l`.
 */
export const UnsubscribePage = () => {
  const [params, setParams] = useSearchParams();
  const lang: UnsubscribeLang = params.get("l") === "en" ? "en" : "fr";
  const [token] = useState(() => params.get("t") ?? "");
  const c = UNSUBSCRIBE_COPY[lang];
  const [state, setState] = useState<State>(
    token ? { kind: "loading" } : { kind: "invalid" },
  );
  const [officer, setOfficer] = useState<Officer>(NO_OFFICER);
  const doneRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    const probeFailed = () =>
      setState({ kind: "ready", masked: null, busy: false, notice: null });
    (async () => {
      try {
        const res = await fetch(
          `${base()}/gavion-unsubscribe?t=${encodeURIComponent(token)}&l=${lang}&format=json`,
          { signal: ctrl.signal },
        );
        if (res.status === 400) return setState({ kind: "invalid" });
        if (!res.ok) return probeFailed();
        const data = await res.json();
        if (data?.privacy_officer) setOfficer(data.privacy_officer);
        if (!data?.valid) return setState({ kind: "invalid" });
        setState({
          kind: "ready",
          masked:
            typeof data.email_masked === "string" ? data.email_masked : null,
          busy: false,
          notice: null,
        });
      } catch {
        if (!ctrl.signal.aborted) probeFailed();
      }
    })();
    return () => ctrl.abort();
  }, [token, lang]);

  useEffect(() => {
    if (state.kind === "done") doneRef.current?.focus();
  }, [state.kind]);

  const confirm = async () => {
    if (state.kind !== "ready") return;
    const { masked } = state;
    const back = (notice: Notice) =>
      setState({ kind: "ready", masked, busy: false, notice });
    setState({ kind: "ready", masked, busy: true, notice: null });
    try {
      const res = await fetch(`${base()}/gavion-unsubscribe`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ t: token }),
      });
      if (res.ok) {
        setState({ kind: "done" });
        // Drop the token from the URL and history once it has been used.
        setParams({ l: lang }, { replace: true });
        return;
      }
      if (res.status === 400) return setState({ kind: "invalid" });
      back(res.status === 429 ? "rate_limited" : "error");
    } catch {
      back("error");
    }
  };

  const noticeText =
    state.kind === "ready" && state.notice
      ? state.notice === "rate_limited"
        ? c.rateLimited
        : c.error
      : null;

  return (
    <div
      lang={lang}
      className="min-h-screen bg-background text-foreground font-['DM_Sans',sans-serif] flex items-start sm:items-center justify-center p-4"
    >
      <main className="w-full max-w-md rounded-lg border bg-card shadow-sm overflow-hidden">
        <div className="h-1 bg-primary" aria-hidden="true" />
        <div className="p-6 space-y-5">
          <p className="text-xl font-bold tracking-tight">Gavion</p>
          <h1 className="text-lg font-semibold">{c.title}</h1>
          <div role="status" aria-live="polite" className="space-y-4">
            {state.kind === "loading" && (
              <p className="text-muted-foreground">{c.loading}</p>
            )}
            {state.kind === "invalid" && <p>{c.invalid}</p>}
            {state.kind === "ready" && (
              <>
                <p>
                  {state.masked ? (
                    <>
                      {c.intro}{" "}
                      <strong className="break-all">{state.masked}</strong>
                    </>
                  ) : (
                    c.introNoAddress
                  )}
                </p>
                {noticeText && <p>{noticeText}</p>}
                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  disabled={state.busy}
                  onClick={confirm}
                >
                  {state.busy ? c.working : state.notice ? c.retry : c.button}
                </Button>
              </>
            )}
            {state.kind === "done" && (
              <>
                <p ref={doneRef} tabIndex={-1} className="font-semibold">
                  {c.done}
                </p>
                <p className="text-muted-foreground">{c.doneDetail}</p>
              </>
            )}
          </div>
          <footer className="border-t pt-4 text-sm text-muted-foreground space-y-1">
            <p>
              {c.questions} {CASL_ORG.contactEmail}
            </p>
            {officer.name && (
              <p>
                {c.privacy} {officer.name}
                {officer.email ? ` (${officer.email})` : ""}
              </p>
            )}
            <p>
              {CASL_ORG.legalName}, {CASL_ORG.postalAddress}
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
};
